import { useCallback, useRef, useState } from "react";
import { Grid, Skeleton, Stack } from "@mui/material";
import { DataGrid, GridPaginationModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../common/routesUtils";
import { View } from "../../../components/layout/View";
import PaperCard from "../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../components/widgets/PaperCard/PaperCardHeader";
import { PageType } from "../../../config/page.types";
import { useBreadcrumbs } from "../../../context/BreadcrumbsProvider";
import { ApiRoutes } from "../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../hooks/useResource";
import { ThemeColors } from "../../../theme/themeValues";
import { colsData, getColumns, getRows } from "./data";

interface SupersetDashboardListDTO {
	items: {
		embeddingId: string;
		name: string;
		rls?: string;
	}[];
	itemsCount: number;
}

const ResultsDashboardInsights = () => {
	const { visitPage } = useBreadcrumbs();

	const { data, isLoading, reload } = useReadResource<undefined, SupersetDashboardListDTO>({
		route: ApiRoutes.SupersetDashboards,
	});

	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("result.dashboardInsights");

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const handlePaginationChange = useCallback((newModel: GridPaginationModel) => {
		setPaginationModel(newModel);
	}, []);

	const navigateTo = useNavigate();

	const handleRowClick = (params: any) => {
		const dashboardId = params.row.id;

		// Update breadcrumb stack
		visitPage(PageType.ResultsDashboardInsightsDetail, false, dashboardId);
		navigateTo(AppRoutes.ResultsDashboardInsightsDetail.replace(":id", dashboardId));
	};

	return (
		<View pageType={PageType.ResultsDashboardInsights}>
			<Grid size={12}>
				{isLoading ? (
					<Skeleton variant="rounded" height={450} width="100%" />
				) : (
					<PaperCard
						ref={containerRef}
						sxContent={{ padding: 0 }}
						header={
							<PaperCardHeader
								title={"Elenco dashboard"}
								isLoading={isLoading}
								onReload={reload}
								containerRef={containerRef}
							/>
						}
						content={
							<Stack sx={{ width: "100%", height: 450 }}>
								<DataGrid
									getRowId={(row) => row.id}
									rows={data ? getRows(data, t) : []}
									columns={getColumns(colsData(t), t)}
									disableColumnMenu
									pagination
									paginationMode="server"
									rowCount={data?.itemsCount || 0}
									pageSizeOptions={[10, 25, 50]}
									paginationModel={paginationModel}
									onPaginationModelChange={handlePaginationChange}
									rowHeight={56}
									hideFooterSelectedRowCount
									checkboxSelection={true}
									onRowClick={handleRowClick}
									disableRowSelectionOnClick
									sx={{
										"& .MuiDataGrid-row:hover": {
											cursor: "pointer",
										},
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: ThemeColors.LightPrimary,
										},

										"& .MuiDataGrid-row:nth-of-type(even)": {
											backgroundColor: ThemeColors.LightBackgroundElevation0,
										},
										"& .MuiDataGrid-scrollbarFiller": {
											display: "none",
										},
										"& .MuiDataGrid-scrollbarFiller--header": {
											display: "none",
										},
									}}
								/>
							</Stack>
						}
					/>
				)}
			</Grid>
		</View>
	);
};

export default ResultsDashboardInsights;
